// API配置文件
// 统一管理所有API请求地址，方便后续修改

/**
 * 获取API基础URL
 * 优先级：环境变量 > 用户访问域名 > 默认值
 *
 * @returns API基础URL
 */
export function getApiBaseUrl(): string {
  if (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_API_BASE_URL) {
    return process.env.NEXT_PUBLIC_API_BASE_URL.trim();
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return 'http://localhost:9332';
}

/**
 * 获取 C 端 API 基础URL（用于读取系统维护状态）
 * 优先级：NEXT_PUBLIC_PUBLIC_API_BASE_URL > NEXT_PUBLIC_API_BASE_URL > 根据当前管理端域名推导 > 默认值
 */
export function getPublicApiBaseUrl(): string {
  if (
    typeof process !== 'undefined' &&
    process.env.NEXT_PUBLIC_PUBLIC_API_BASE_URL
  ) {
    return process.env.NEXT_PUBLIC_PUBLIC_API_BASE_URL.trim();
  }

  if (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_API_BASE_URL) {
    return process.env.NEXT_PUBLIC_API_BASE_URL.trim();
  }

  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (origin.includes('test-admin-api.')) {
      return origin.replace('test-admin-api.', 'test-api.');
    }
    if (origin.includes('admin-api.')) {
      return origin.replace('admin-api.', 'node-api.');
    }
  }

  return 'http://localhost:9332';
}

// ==================== API 基础URL配置 ====================
// 使用说明：
// 优先级：环境变量 > 手动配置 > 默认值
// 1. 环境变量：在 .env.local 中设置 NEXT_PUBLIC_API_BASE_URL（推荐）
// 2. 手动配置：取消注释下面的 API_BASE_URL
// 3. 默认值：http://localhost:9332

// 开发环境（本地开发）- 如需使用，请取消注释
// export const API_BASE_URL = 'http://192.168.3.60:8000';
// export const API_BASE_URL = 'http://192.168.3.97:8000';

// 测试环境 - 如需使用，请取消注释
// export const API_BASE_URL = 'https://sitback.apg.network';

// 生产环境 - 如需使用，请取消注释
// export const API_BASE_URL = 'https://backend.apg.pro';

// 默认未设置，将使用环境变量或回退到 http://localhost:9332
export const API_BASE_URL: string | undefined = undefined;

// API端点配置
export const API_ENDPOINTS = {
  // 认证相关
  LOGIN: '/admin/api/v1/login',
  LOGIN_NONCE: '/admin/api/v1/login/nonce',
  PROFILE: '/admin/api/v1/profile',

  // 任务管理相关
  SCHEDULER_STATUS: '/admin/api/v1/scheduler/status',
  SCHEDULER_RELOAD: '/admin/api/v1/scheduler/reload',
  SCHEDULER_TASKS: '/admin/api/v1/scheduler/tasks',
  SCHEDULER_LOGS: '/admin/api/v1/scheduler/logs',

  // 用户管理相关
  USER_LIST: '/admin/api/v1/admin/user/list',
  USER_UPDATE_PARENT: '/admin/api/v1/admin/user/update-parent-invite-code',
  USER_PERFORMANCE: '/admin/api/v1/admin/user/sub-performance',
  USER_REALTIME_STATS: '/admin/api/v1/admin/user/realtime-stats',
  SET_SERVICE_CENTER: '/admin/api/v1/admin/user/set-service-center',
  UPDATE_WALLET_ADDRESS: '/admin/api/v1/admin/user/update-wallet-address',
  REPLACE_WALLET_ADDRESS: '/admin/api/v1/admin/user/replace-wallet-address',
  ADDRESS_UPDATE_LOGS: '/admin/api/v1/admin/user/address-update-logs',
  PARENT_UPDATE_LOGS: '/admin/api/v1/admin/user/parent-update-logs',
  UPDATE_TEAM_CAN_WITHDRAW: '/admin/api/v1/admin/user/update-team-can-withdraw',
  UPDATE_TEAM_CAN_SWAP: '/admin/api/v1/admin/user/update-team-can-swap',
  UPDATE_TEAM_STAKE_RATE: '/admin/api/v1/admin/user/update-team-stake-rate',
  SET_USE_FULL_PERF: '/admin/api/v1/admin/user/set-use-full-perf',
  SET_WHITE_LIST: '/admin/api/v1/admin/user/set-white-list',
  SET_MARKET_LEADER: '/admin/api/v1/admin/user/set-market-leader',
  SET_USER_PROMOTE_TYPE: '/admin/api/v1/admin/user/set-promote-type',
  USER_PROMOTE_TYPE_LIST: '/admin/api/v1/admin/user/promote-type-list',
  // 业绩历史相关
  PERFORMANCE_HISTORY: '/admin/api/v1/admin/user/performance-history',
  // 业绩统计相关
  PERFORMANCE_STATS: '/admin/api/v1/admin/user/performance-stats',
  // 等级调整相关
  DIAMOND_LEVEL_ADJUSTMENT_CREATE:
    '/admin/api/v1/admin/diamond-level-adjustment/create',
  DIAMOND_LEVEL_ADJUSTMENT_LIST:
    '/admin/api/v1/admin/diamond-level-adjustment/list',
  DIAMOND_LEVEL_ADJUSTMENT_CANCEL:
    '/admin/api/v1/admin/diamond-level-adjustment/cancel',
  DIAMOND_LEVEL_ADJUSTMENT_EXPORT:
    '/admin/api/v1/admin/diamond-level-adjustment/export',
  // 昨日直推排行
  YESTERDAY_WEIGHTED_RANKING:
    '/admin/api/v1/admin/user/yesterday-weighted-ranking',
  // 资金记录 - USDT明细
  RELEASE_USDT_DETAILS: '/admin/api/v1/admin/user/release-usdt-details',
  RELEASE_USDT_DETAIL: '/admin/api/v1/admin/user/release-usdt-detail',
  // 资金记录 - 充值记录
  RECHARGE_RECORDS: '/admin/api/v1/admin/user/recharge-records',
  RECHARGE_RECORDS_EXPORT: '/admin/api/v1/admin/user/recharge-records/export',
  // 资金记录 - 充值退回触发
  TRIGGER_RECHARGE_REFUND: '/admin/api/v1/cobo/refund/trigger',
  // 资金记录 - 提现记录
  WITHDRAW_RECORDS: '/admin/api/v1/admin/user/withdraw-records',
  WITHDRAW_RECORDS_EXPORT: '/admin/api/v1/admin/user/withdraw-records/export',
  // 资金记录 - 盈利税统计汇总
  PROFIT_TAX_SUMMARY: '/admin/api/v1/admin/user/profit-tax-summary',
  // 资金记录 - 提现统计
  WITHDRAW_SUMMARY: '/admin/api/v1/admin/user/withdraw-summary',
  WITHDRAW_RANKING: '/admin/api/v1/admin/user/withdraw-ranking',
  RECENT_FLOW: '/admin/api/v1/admin/user/recent-flow',
  DAILY_RANKING: '/admin/api/v1/admin/user/daily-ranking',
  WITHDRAW_TREND: '/admin/api/v1/admin/user/withdraw-trend',
  // 资金记录 - 收益转本金记录
  PRINCIPAL_TRANSFER_LIST: '/admin/api/v1/principal_transfer/list',
  // 资金记录 - 第三方划转记录
  THIRD_PARTY_TRANSFER_RECORDS: '/admin/api/v1/third-party-transfer/records',
  THIRD_PARTY_TRANSFER_STATS: '/admin/api/v1/third-party-transfer/stats',
  THIRD_PARTY_TRANSFER_TREND: '/admin/api/v1/third-party-transfer/trend',

  // 资金记录 - 质押统计
  STAKING_SUMMARY: '/admin/api/v1/admin/user/staking-summary',
  // 区块链交易查询
  QUERY_STAKE_EVENT_BY_HASH: '/admin/api/v1/staking/query-event',
  // 提现区块链交易查询
  QUERY_WITHDRAW_EVENT_BY_HASH: '/admin/api/v1/withdraw/query-event',
  // 取消提现
  WITHDRAW_CANCEL: '/admin/api/v1/withdraw/cancel',
  // Cobo 提现审核
  COBO_WITHDRAW_AUDIT: '/admin/api/v1/cobo/withdraw/audit',
  // 退款管理
  REFUND_RECORDS: '/admin/api/v1/cobo/refund/records',
  REFUND_TRIGGER: '/admin/api/v1/cobo/refund/trigger',
  // 提现地址白名单
  WITHDRAW_WHITE_LIST: '/admin/api/v1/withdraw-white-list',
  // 节点信息列表
  NODE_INFO_LIST: '/admin/api/v1/admin/user/node-info-list',

  // 用户详情相关
  USER_DETAIL: '/admin/api/v1/admin/user/detail',
  USER_ASSET_OVERVIEW: '/admin/api/v1/admin/user/asset-overview',
  USER_REWARD_SUMMARY: '/admin/api/v1/admin/user/reward-summary',
  USER_RECORD_COUNTS: '/admin/api/v1/admin/user/record-counts',
  USER_STAKING_RECORDS: '/admin/api/v1/admin/user/staking-records',
  USER_REWARD_RECORDS: '/admin/api/v1/admin/user/reward-records',
  USER_BALANCE_CHANGE_LOGS: '/admin/api/v1/admin/user/balance-change-logs',
  USER_INVITE_RECORDS: '/admin/api/v1/admin/user/invite-records',
  USER_MINT_RECORDS: '/admin/api/v1/admin/user/mint-records',
  USER_NODE_PURCHASE_RECORDS: '/admin/api/v1/admin/user/node-purchase-records',
  USER_NODE_EXEMPT: '/admin/api/v1/admin/user/node-exempt',
  USER_NODE_CHEAP_NODE: '/admin/api/v1/node/cheap-node',
  USER_WITHDRAW_QUOTA: '/admin/api/v1/admin/user/withdraw-quota',
  USER_WITHDRAW_QUOTA_RESET: '/admin/api/v1/admin/user/withdraw-quota/reset',
  USER_PASSWORD_RESET: '/admin/api/v1/admin/user/password/reset',
  USER_DEPOSIT_ADDRESS: '/admin/api/v1/admin/user/deposit-address',
  USER_MARK_TEAM: '/admin/api/v1/admin/user/mark-team',
  USER_TRANSFER_RECORDS: '/admin/api/v1/admin/user/transfer-records',
  USER_SWAP_RECORDS: '/admin/api/v1/admin/user/swap-records',
  USER_CLAIM_RECORDS: '/admin/api/v1/admin/user/claim-records',
  USER_LP_GRANT_RECORDS: '/admin/api/v1/admin/user/lp-grant-records',

  // 网体查询相关
  NETWORK_DESCENDANTS: '/admin/api/v1/admin/user/descendants',
  // 服务中心相关
  SERVICE_CENTER_LIST: '/admin/api/v1/admin/user/service-center-list',
  CANCEL_SERVICE_CENTER: '/admin/api/v1/admin/user/cancel-service-center',
  SET_SERVICE_CENTER_RATE: '/admin/api/v1/admin/user/set-service-center-rate',

  // 失败事件相关
  FAILED_EVENTS_LIST: '/admin/api/v1/admin/failed-event/list',

  // 公告管理相关
  ANNOUNCEMENT_LIST: '/admin/api/v1/announcement',
  ANNOUNCEMENT_LANGUAGES: '/admin/api/v1/announcement/languages',
  ANNOUNCEMENT_DETAIL: (id: number) => `/admin/api/v1/announcement/${id}`,
  ANNOUNCEMENT_DELETE: (id: number) => `/admin/api/v1/announcement/${id}`,

  // 配置管理相关
  CONFIG_LIST: '/admin/api/v1/config/list',
  CONFIG_DETAIL: '/admin/api/v1/config/detail',
  CONFIG_UPDATE: '/admin/api/v1/config/update',

  // 代币配置管理相关
  TOKEN_CONFIG_LIST: '/admin/api/v1/token-config/list',
  TOKEN_CONFIG_DETAIL: '/admin/api/v1/token-config/detail',
  TOKEN_CONFIG_CREATE: '/admin/api/v1/token-config/create',
  TOKEN_CONFIG_UPDATE: '/admin/api/v1/token-config/update',
  TOKEN_CONFIG_RECHARGE_RANK: '/admin/api/v1/token-config/recharge-rank',
  TOKEN_CONFIG_FETCH_INFO: '/admin/api/v1/token-config/fetch-info',
  TOKEN_CONFIG_DEFAULT_DEPOSIT_ADDRESS:
    '/admin/api/v1/token-config/default-deposit-address',

  // 联系方式管理相关
  CONTACT_LIST: '/admin/api/v1/contact/list',
  CONTACT_UPDATE: '/admin/api/v1/contact/update',

  // 会议管理相关
  MEETING_LIST: '/admin/api/v1/meeting',
  MEETING_DETAIL: (id: number) => `/admin/api/v1/meeting/${id}`,
  MEETING_AUDIT: '/admin/api/v1/meeting/audit',
  MEETING_MATERIAL_AUDIT: '/admin/api/v1/meeting/material-audit',
  MEETING_ACCEPTANCE: '/admin/api/v1/meeting/acceptance',
  MEETING_REIMBURSEMENT_LIST: '/admin/api/v1/meeting-reimbursement',
  MEETING_REIMBURSEMENT_DETAIL: (id: number) =>
    `/admin/api/v1/meeting-reimbursement/${id}`,
  MEETING_REIMBURSEMENT_AUDIT: '/admin/api/v1/meeting-reimbursement/audit',
  MEETING_REIMBURSEMENT_ACTIVITY_LIST:
    '/admin/api/v1/meeting-reimbursement/activity/list',
  MEETING_REIMBURSEMENT_ACTIVITY_CREATE:
    '/admin/api/v1/meeting-reimbursement/activity/create',
  MEETING_REIMBURSEMENT_ACTIVITY_UPDATE:
    '/admin/api/v1/meeting-reimbursement/activity/update',
  MEETING_REIMBURSEMENT_ACTIVITY_ACTIVATE:
    '/admin/api/v1/meeting-reimbursement/activity/activate',
  OSS_PRESIGN_GET: '/admin/api/v1/oss/presign-get',
  ADMIN_UPLOAD_IMAGE: '/admin/api/v1/upload/image',

  // 白皮书管理相关
  WHITEPAPER_LIST: '/admin/api/v1/whitepaper/list',
  WHITEPAPER_CREATE: '/admin/api/v1/whitepaper/create',
  WHITEPAPER_UPDATE: '/admin/api/v1/whitepaper/update',
  WHITEPAPER_DELETE: '/admin/api/v1/whitepaper/delete',

  // 饭桌分享会
  TABLE_SHARE_LIST: '/admin/api/v1/table-share',
  TABLE_SHARE_DETAIL: (id: number) => `/admin/api/v1/table-share/${id}`,
  TABLE_SHARE_AUDIT: '/admin/api/v1/table-share/audit',
  TABLE_SHARE_CONFIG: '/admin/api/v1/table-share/config',
  TABLE_SHARE_PERFORMANCE_OCCUPANCY:
    '/admin/api/v1/table-share/performance-occupancy',
  TABLE_SHARE_PERFORMANCE_RANGE: '/admin/api/v1/table-share/performance-range',

  // 工作室申请
  OFFICE_APPLY_LIST: '/admin/api/v1/office-apply',
  OFFICE_APPLY_DETAIL: (id: number) => `/admin/api/v1/office-apply/${id}`,
  OFFICE_APPLY_AUDIT: '/admin/api/v1/office-apply/audit',
  OFFICE_APPLY_CONFIG: '/admin/api/v1/office-apply/config',

  // 办公室打卡
  OFFICE_CLOCK_IN_LIST: '/admin/api/v1/office-clock-in',
  OFFICE_CLOCK_IN_DETAIL: (id: number) => `/admin/api/v1/office-clock-in/${id}`,
  OFFICE_CLOCK_IN_CONFIG: '/admin/api/v1/office-clock-in/config',

  // 视频共创打卡
  VIDEO_CO_CREATION_LIST: '/admin/api/v1/video-co-creation',
  VIDEO_CO_CREATION_DETAIL: (id: number) =>
    `/admin/api/v1/video-co-creation/${id}`,
  VIDEO_CO_CREATION_AUDIT: '/admin/api/v1/video-co-creation/audit',
  VIDEO_CO_CREATION_CONFIG: '/admin/api/v1/video-co-creation/config',

  // Bet 账户管理
  BET_OPEN: '/admin/api/v1/bet/open',
  BET_APPLICATIONS: '/admin/api/v1/bet/applications',
  BET_ACCOUNTS: '/admin/api/v1/bet/accounts',
  BET_AUDIT: '/admin/api/v1/bet/audit',
  BET_TRANSFER: '/admin/api/v1/bet/transfer',

  // 代币发行申请
  TOKEN_ISSUANCE_LIST: '/admin/api/v1/token-issuance',
  TOKEN_ISSUANCE_DETAIL: (id: number) => `/admin/api/v1/token-issuance/${id}`,
  TOKEN_ISSUANCE_AUDIT: '/admin/api/v1/token-issuance/audit',
  TOKEN_ISSUANCE_LISTED: (id: number) =>
    `/admin/api/v1/token-issuance/${id}/listed`,
  TOKEN_ISSUANCE_EXPORT: '/admin/api/v1/token-issuance/export',
  TOKEN_ISSUANCE_CONFIG: '/admin/api/v1/token-issuance/config',

  // 多级审批记录
  ADMIN_APPROVAL_RECORDS: '/admin/api/v1/admin-approval/records',
  ADMIN_APPROVAL_STATISTICS: '/admin/api/v1/admin-approval/statistics',

  // 质押管理相关
  STAKING_LIST: '/admin/api/v1/staking/list',
  STAKING_STATS: '/admin/api/v1/staking/stats',
  STAKING_IMPORT: '/admin/api/v1/staking/import/node',
  STAKING_ADJUST: '/admin/api/v1/staking/adjust',
  // 质押代币列表
  STAKING_TOKEN_LIST: '/admin/api/v1/staking/token/category',

  // 首页统计
  HOME_STATS: '/admin/api/v1/home/stats',

  // 节点赠送
  NODE_GIFT: '/admin/api/v1/node/gift',
  NODE_GIFT_LIST: '/admin/api/v1/node/gift/list',
  NODE_GIFT_DELETE: '/admin/api/v1/node/gift/delete',

  // 管理员管理相关
  ADMIN_LIST: '/admin/api/v1/perm/admin/list',
  ADMIN_CREATE: '/admin/api/v1/perm/admin/create',
  ADMIN_UPDATE: '/admin/api/v1/perm/admin/update',

  // 角色管理相关
  ROLE_LIST: '/admin/api/v1/perm/role/list',
  ROLE_CREATE: '/admin/api/v1/perm/role/create',
  ROLE_DELETE: '/admin/api/v1/perm/role/delete',
  ROLE_DETAIL: '/admin/api/v1/perm/role/detail',
  ROLE_UPDATE: '/admin/api/v1/perm/role/update',

  // 菜单管理相关
  MENU_LIST: '/admin/api/v1/perm/menu_function/list',
  MENU_CREATE: '/admin/api/v1/perm/menu_function/create',
  MENU_UPDATE: '/admin/api/v1/perm/menu_function/update',
  MENU_DELETE: '/admin/api/v1/perm/menu_function/delete',
  MENU_FUNCTION: '/admin/api/v1/perm/admin/menu-function',

  // 操作日志相关
  OPERATION_LOG_LIST: '/admin/api/v1/operation-log/list',

  // 拼团游戏相关
  MINT_MATCH_LIST: '/admin/api/v1/mint/match/list',
  MINT_MATCH_DETAIL: '/admin/api/v1/mint/match/detail',
  MINT_PLAYER_LIST: '/admin/api/v1/mint/player/list',
  MINT_REFERRAL_LIST: '/admin/api/v1/mint/referral/list',
  MINT_CLAIMED_LIST: '/admin/api/v1/mint/claimed/list',
  MINT_STAKED_LIST: '/admin/api/v1/mint/staked/list',

  // 拼团币种管理
  MINT_TOKEN_LIST: '/admin/api/v1/mint/token/list',
  MINT_TOKEN_REFRESH: '/admin/api/v1/mint/token/refresh',
  MINT_TOKEN_UPDATE_ICON: '/admin/api/v1/mint/token/icon',

  // 拼团合约管理
  MINT_CONTRACT_LIST: '/admin/api/v1/mint/contract/list',
  MINT_CONTRACT_ACTIVE: '/admin/api/v1/mint/contract/active',
  MINT_CONTRACT_SET_ACTIVE: '/admin/api/v1/mint/contract/set-active',

  // 拼团场次控制
  MINT_MATCH_RESET: '/admin/api/v1/mint/match/reset',
  MINT_MATCH_ERASE: '/admin/api/v1/mint/match/erase',
  MINT_MATCH_DRAW: '/admin/api/v1/mint/match/draw',
  MINT_MATCH_RESYNC_PREVIEW: '/admin/api/v1/mint/match/resync-preview',
  MINT_MATCH_RESYNC_EXECUTE: '/admin/api/v1/mint/match/resync-execute',

  // 拼团管理 - 团队拼购单数
  GROUP_MATCH_TEAM_ORDER_COUNT: '/admin/api/v1/group_purchase/team_order_count',

  // 团队管理相关
  TEAM_LIST: '/admin/api/v1/team/list',
  TEAM_DETAIL: '/admin/api/v1/team/detail',
  TEAM_CREATE: '/admin/api/v1/team/create',
  TEAM_UPDATE: '/admin/api/v1/team/update',
  TEAM_DELETE: '/admin/api/v1/team/delete',
  TEAM_WITHDRAW_SWITCH: '/admin/api/v1/team/withdraw-switch',
  TEAM_STATS: '/admin/api/v1/team/stats',
  TEAM_STAKE_DETAILS: '/admin/api/v1/team/stake-details',
  TEAM_WITHDRAW_DETAILS: '/admin/api/v1/team/withdraw-details',
  // 团队指标相关
  TEAM_METRICS_LIST: '/admin/api/v1/user-team-metrics',
  TEAM_METRICS_CREATE: '/admin/api/v1/user-team-metrics',
  TEAM_METRICS_DELETE: (id: number) => `/admin/api/v1/user-team-metrics/${id}`,
  // 团队趋势
  TEAM_DAILY_TREND: '/admin/api/v1/team/daily-trend',
  // 团队汇总
  TEAM_SUMMARY: '/admin/api/v1/team/summary',
  TEAM_STAKEV2_STATS: '/admin/api/v1/team/stakev2-stats',
  ORG_CHART: '/admin/api/v1/org/chart',
  ORG_CHART_XMIND: '/admin/api/v1/org/chart/xmind',

  // 质押V2管理
  STAKING_V2_LIST: '/admin/api/v1/staking_v2/list',
  STAKING_V2_STATS: '/admin/api/v1/staking_v2/stats',
  STAKING_V2_TREND: '/admin/api/v1/staking_v2/trend',
  STAKING_V2_BACKFILL: '/admin/api/v1/staking_v2/backfill',
  STAKING_V2_STATS_REFRESH: '/admin/api/v1/staking_v2/stats/refresh',
  STAKING_V2_GIFT: '/admin/api/v1/staking_v2/gift',

  // 三倍券7月活动赠送详情（只读）
  VIP_PROMO_GRANTS: '/admin/api/v1/vip_promo/grants',

  // AI养牛管理
  AI_COW_CONFIG: '/admin/api/v1/ai-cow/config',
  AI_COW_GIFT: '/admin/api/v1/ai-cow/gift',
  AI_COW_BAN: '/admin/api/v1/ai-cow/ban',
  AI_COW_USERS: '/admin/api/v1/ai-cow/users',
  AI_COW_STATS: '/admin/api/v1/ai-cow/stats',
  AI_COW_FEE_STATS: '/admin/api/v1/ai-cow/fee-stats',
  AI_COW_FEE_SUMMARY: '/admin/api/v1/ai-cow/fee-summary',
  AI_COW_LOGS: '/admin/api/v1/ai-cow/logs',
  AI_COW_CLEAR: '/admin/api/v1/ai-cow/clear',

  // 游戏节点管理
  GAME_NODE_SALES_SUMMARY: '/admin/api/v1/game-node/sales-summary',
  GAME_NODE_PURCHASE_RECORDS: '/admin/api/v1/game-node/purchase-records',

  // Cobo 节点统计
  COBO_NODE_SALES_SUMMARY: '/admin/api/v1/cobo/node/sales-summary',
  COBO_NODE_PURCHASE_RECORDS: '/admin/api/v1/cobo/node/purchase-records',
  COBO_NODE_TEAM_PURCHASE_SUMMARY:
    '/admin/api/v1/cobo/node/team-purchase-summary',

  // 拼团管理
  GROUP_MATCH_RANKING: '/admin/api/v1/group_purchase/ranking',
  GROUP_MATCH_RANKING_REWARDS: '/admin/api/v1/group_purchase/ranking_rewards',
  GROUP_MATCH_RANKING_REWARD_CONFIG:
    '/admin/api/v1/group_purchase/ranking/reward-config',
  GROUP_MATCH_SESSIONS: '/admin/api/v1/group_purchase/sessions',
  GROUP_MATCH_SESSION_ORDERS: '/admin/api/v1/group_purchase/session/orders',
  GROUP_MATCH_USER_ORDERS: '/admin/api/v1/group_purchase/user/orders',
  GROUP_MATCH_SESSION_SETTLE: '/admin/api/v1/group_purchase/session/settle',
  GROUP_MATCH_SESSION_UNSETTLE: '/admin/api/v1/group_purchase/session/unsettle',
  GROUP_MATCH_SESSION_CLEAR: '/admin/api/v1/group_purchase/session/clear',
  GROUP_MATCH_GROUP_DETAIL: '/admin/api/v1/group_purchase/group/detail',
  GROUP_MATCH_TEAM_REWARD_STATS:
    '/admin/api/v1/group_purchase/team_reward/stats',
  GROUP_MATCH_TEAM_REWARD_SESSIONS:
    '/admin/api/v1/group_purchase/team_reward/sessions',
  GROUP_MATCH_TEAM_REWARD_DISTRIBUTIONS:
    '/admin/api/v1/group_purchase/team_reward/distributions',
  GROUP_MATCH_TEAM_REWARD_SINK_STATS:
    '/admin/api/v1/group_purchase/team_reward/sink_stats',
  GROUP_MATCH_TEAM_REWARD_SINK_RECORDS:
    '/admin/api/v1/group_purchase/team_reward/sink_records',

  // 预备奖管理
  GROUP_MATCH_RESERVE_REWARD_STATS:
    '/admin/api/v1/group_purchase/reserve_reward/stats',
  GROUP_MATCH_RESERVE_REWARD_DAILY:
    '/admin/api/v1/group_purchase/reserve_reward/daily',
  GROUP_MATCH_RESERVE_REWARD_DISTRIBUTIONS:
    '/admin/api/v1/group_purchase/reserve_reward/distributions',

  // 领导奖管理
  GROUP_MATCH_LEADERSHIP_POOL_STATS:
    '/admin/api/v1/group_purchase/leadership/pool_stats',
  GROUP_MATCH_LEADERSHIP_DISTRIBUTE:
    '/admin/api/v1/group_purchase/leadership/distribute',
  GROUP_MATCH_LEADERSHIP_CONFIG:
    '/admin/api/v1/group_purchase/leadership/config',
  GROUP_MATCH_LEADERSHIP_DISTRIBUTED:
    '/admin/api/v1/group_purchase/leadership/distributed',
  GROUP_MATCH_LEADERSHIP_DAILY_STATS:
    '/admin/api/v1/group_purchase/leadership/daily_stats',
  GROUP_MATCH_LEADERSHIP_PENDING:
    '/admin/api/v1/group_purchase/leadership/pending',

  // 平级奖管理
  GROUP_MATCH_LEADERSHIP_PEER_STATS:
    '/admin/api/v1/group_purchase/leadership/peer/stats',
  GROUP_MATCH_LEADERSHIP_PEER_DAILY:
    '/admin/api/v1/group_purchase/leadership/peer/daily',
  GROUP_MATCH_LEADERSHIP_PEER_DISTRIBUTIONS:
    '/admin/api/v1/group_purchase/leadership/peer/distributions',

  // 矿池释放管理
  GROUP_MATCH_MINING_POOL_RELEASE:
    '/admin/api/v1/group_purchase/mining_pool_release/trigger',
  GROUP_MATCH_MINING_POOL_RELEASE_STATS:
    '/admin/api/v1/group_purchase/mining_pool_release/stats',
  GROUP_MATCH_MINING_POOL_DAILY_RELEASE:
    '/admin/api/v1/group_purchase/mining_pool_release/daily',
  GROUP_MATCH_MINING_POOL_USERS_EXPORT:
    '/admin/api/v1/group_purchase/mining_pool_release/users/export',

  // GAS销毁管理
  GROUP_MATCH_TICKET_BURN_LIST: '/admin/api/v1/group_purchase/ticket_burn/list',
  GROUP_MATCH_TICKET_BURN_DETAIL:
    '/admin/api/v1/group_purchase/ticket_burn/detail',
  GROUP_MATCH_TICKET_BURN_STATS:
    '/admin/api/v1/group_purchase/ticket_burn/stats',

  // 系统维护管理
  SYSTEM_MAINTENANCE_GET: '/admin/api/v1/system/maintenance',
  SYSTEM_MAINTENANCE_SET: '/admin/api/v1/system/maintenance',

  // 代币价格管理
  TOKEN_PRICE_LIST: '/admin/api/v1/token-price/list',
  TOKEN_PRICE_GET: '/admin/api/v1/token-price',
  TOKEN_PRICE_SET: '/admin/api/v1/token-price',
  TOKEN_PRICE_OP_LOGS: '/admin/api/v1/token-price/op-logs',
  TOKEN_PRICE_OPERATORS: '/admin/api/v1/token-price/operators',

  // 平台钱包余额
  UPAY_WALLET_BALANCE: '/admin/api/v1/upay/wallet-balance',

  // YY价格管理
  YY_PRICE_GET: '/admin/api/v1/yy-price',
  YY_PRICE_SET: '/admin/api/v1/yy-price',
  YY_MIN_TRADE_AMOUNT_GET: '/admin/api/v1/yy-min-trade-amount',
  YY_MIN_TRADE_AMOUNT_SET: '/admin/api/v1/yy-min-trade-amount',
  YY_SWAP_FEE_GET: '/admin/api/v1/yy-swap-fee',
  YY_SWAP_FEE_SET: '/admin/api/v1/yy-swap-fee',
  YY_SWAP_FEE_ADDR_GET: '/admin/api/v1/yy-swap-fee-addr',
  YY_SWAP_FEE_ADDR_SET: '/admin/api/v1/yy-swap-fee-addr',
  YY_CONFIG_GET: '/admin/api/v1/yy-config',
  YY_CONFIG_SET: '/admin/api/v1/yy-config',

  // LP 管理
  LP_VAULT: '/admin/api/v1/lp/vault',
  LP_VAULT_CREATE: '/admin/api/v1/lp/vault/create',
  LP_DAILY_LIST: '/admin/api/v1/lp/daily/list',
  LP_DAILY_DETAIL: '/admin/api/v1/lp/daily/detail',
  LP_SESSION_SYNC_LIST: '/admin/api/v1/lp/session-sync/list',
  LP_CONTRACT_UPDATE: '/admin/api/v1/lp/contract/update',
  LP_PROJECT_OVERVIEW: '/admin/api/v1/lp/project-overview',
  LP_CLAIM_SUMMARY: '/admin/api/v1/lp/claim/summary',
  LP_CLAIM_LIST: '/admin/api/v1/lp/claim/list',
  LP_REWARD_CLAIM_LIST: '/admin/api/v1/lp/reward-claim/list',

  // 股票回馈
  STOCK_VAULT: '/admin/api/v1/stock/vault',
  STOCK_CONTRACT_UPDATE: '/admin/api/v1/stock/contract/update',
  STOCK_SYNC_RECORDS: '/admin/api/v1/stock/sync-records',
  STOCK_REWARD_RECORDS: '/admin/api/v1/stock/reward-records',

  // 税池统计
  TAX_POOL_RECORDS: '/admin/api/v1/tax-pool/records',
  TAX_POOL_NODE_DIVIDEND: '/admin/api/v1/tax-pool/node-dividend'
} as const;

// 构建完整的API URL
// 自动处理 API_BASE_URL 和动态获取的逻辑
export function buildApiUrl(endpoint: string): string {
  const baseUrl = getApiBaseUrl();
  // 确保 endpoint 以 / 开头
  const normalizedEndpoint = endpoint.startsWith('/')
    ? endpoint
    : `/${endpoint}`;
  return `${baseUrl}${normalizedEndpoint}`;
}

// 获取认证token
export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  const storage = (window as any).localStorage;
  if (!storage || typeof storage.getItem !== 'function') return null;
  return storage.getItem('apex_token');
}

export function setAuthToken(token: string): void {
  if (typeof window === 'undefined') return;
  const storage = (window as any).localStorage;
  if (!storage || typeof storage.setItem !== 'function') return;
  storage.setItem('apex_token', token);
}

export function removeAuthToken(): void {
  if (typeof window === 'undefined') return;
  const storage = (window as any).localStorage;
  if (!storage || typeof storage.removeItem !== 'function') return;
  storage.removeItem('apex_token');
}

// 通用请求头配置
export function getRequestHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

// API响应接口定义
export interface ApiResponse<T = any> {
  code?: number;
  msg?: string;
  message?: string;
  data?: T;
  [key: string]: any;
}

// 统一的API请求函数，自动检查code字段
export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {},
  defaultErrorMessage?: string
): Promise<T> {
  // 使用 buildApiUrl 统一构建URL
  const url = buildApiUrl(endpoint);

  const headers = {
    ...getRequestHeaders(),
    ...(options.headers as Record<string, string>)
  };

  const response = await fetch(url, {
    headers,
    ...options
  });

  // 解析响应
  let responseData: ApiResponse<T>;
  try {
    responseData = await response.json();
  } catch (error) {
    // 如果不是JSON格式，抛出错误
    throw new Error(
      defaultErrorMessage || `请求失败: 服务器返回了非JSON格式的响应`
    );
  }

  // 检查HTTP状态码
  if (!response.ok) {
    const errorMsg =
      responseData.msg ||
      responseData.message ||
      defaultErrorMessage ||
      `HTTP错误! 状态码: ${response.status}`;
    throw new Error(errorMsg);
  }

  // 检查业务状态码：code为0才是成功，其他都是错误
  if (responseData.code !== undefined && responseData.code !== 0) {
    const errorMsg =
      responseData.msg ||
      responseData.message ||
      defaultErrorMessage ||
      '操作失败';
    throw new Error(errorMsg);
  }

  // 如果响应中有data字段，返回data；否则返回整个响应
  return (
    responseData.data !== undefined ? responseData.data : responseData
  ) as T;
}
